import { ref, computed, type ComputedRef, type Ref } from 'vue';
import { isNil } from 'lodash';

import {
  YardTaskCheckLocationOutcome,
  AssetContainerEventType,
  AssetContainerState,
  type Nullable,
  type ISpot
} from '@nova/core';
import {
  useDialogTrigger,
  type OpenAssetCheckWizardEvent
} from '@/composables/useEventBasedDialog';
import {
  useRecordCheckAssetLocationOutcomeMutation,
  useCreateAssetContainerEventMutation,
  usePatchAssetContainerMutation,
  useReassignSpotAssignmentMutation,
  useCreateSpotAssignmentMutation
} from '@/composables/useCheckTaskMutations';

import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenter';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import type { ReportedMissingAssetEntryPoint } from '@/analytics/events';

export type WizardStep = 1 | 2 | 3;

export const WIZARD_STEPS = {
  LOCATION: 1,
  STATUS: 2,
  STATE: 3
} as const satisfies Record<string, WizardStep>;

/** Sentinel for "Not required for this check" — no API call is made for that step. */
export const WIZARD_SKIP = '__skip__' as const;

export const enum LocationOutcome {
  Confirmed = 'confirmed',
  Updated = 'updated'
}

/** Step 2 options: all AssetContainerEventType values except Missing and Departed. */
export const WIZARD_STATUS_OPTIONS = [
  AssetContainerEventType.Arrived,
  AssetContainerEventType.Loading,
  AssetContainerEventType.LoadingComplete,
  AssetContainerEventType.Unloading,
  AssetContainerEventType.UnloadingComplete,
  AssetContainerEventType.ReadyForPickup,
  AssetContainerEventType.Inoperable
] as const;

export type WizardStatus = (typeof WIZARD_STATUS_OPTIONS)[number];

function resolveActiveSpotAssignmentId(asset: YardAssetDetails | null | undefined): string | null {
  const active = asset?.spotAssignments?.find(sa => sa.isActive);
  return active?.id ?? null;
}

export interface UseAssetCheckWizardOptions {
  dialogProps: Ref<Nullable<OpenAssetCheckWizardEvent>>;
  asset: Ref<Nullable<YardAssetDetails>>;
  finishCheck: () => void;
  locationConfirmedLabel: string;
  locationUpdatedLabel: string;
  /** Location work is refused while this is true: an asset between spots has none to record. */
  isLocationBlocked?: Ref<boolean> | ComputedRef<boolean>;
  entryPoint?: ReportedMissingAssetEntryPoint;
}

export function useAssetCheckWizard({
  dialogProps,
  asset,
  finishCheck,
  locationConfirmedLabel,
  locationUpdatedLabel,
  isLocationBlocked,
  entryPoint = 'task_detail'
}: UseAssetCheckWizardOptions) {
  const isLocationRefused = () => Boolean(isLocationBlocked?.value);
  const step = ref<WizardStep>(WIZARD_STEPS.LOCATION);
  const selectedStatus = ref<WizardStatus | typeof WIZARD_SKIP | null>(null);
  const selectedState = ref<AssetContainerState | typeof WIZARD_SKIP | null>(null);
  const locationOutcome = ref<LocationOutcome | null>(null);
  const locationOutcomeLabel = computed(() => {
    if (locationOutcome.value === LocationOutcome.Confirmed) {
      return locationConfirmedLabel;
    }
    if (locationOutcome.value === LocationOutcome.Updated) {
      return locationUpdatedLabel;
    }
    return null;
  });

  const stepErrors = ref<Record<WizardStep, string | null>>({
    [WIZARD_STEPS.LOCATION]: null,
    [WIZARD_STEPS.STATUS]: null,
    [WIZARD_STEPS.STATE]: null
  });

  const { triggerDialog: triggerSpotDialog } = useDialogTrigger('open-select-spot-dialog');
  const recordOutcomeMutation = useRecordCheckAssetLocationOutcomeMutation();
  const createEventMutation = useCreateAssetContainerEventMutation();
  const patchContainerMutation = usePatchAssetContainerMutation();
  const reassignMutation = useReassignSpotAssignmentMutation();
  const createAssignmentMutation = useCreateSpotAssignmentMutation();
  const { trackYardTaskAssetReportedMissing } = useYardTaskAnalytics();

  const isReassigningSpot = computed(
    (): boolean => reassignMutation.isPending.value || createAssignmentMutation.isPending.value
  );

  const isMutatingStep1 = computed(
    (): boolean => recordOutcomeMutation.isPending.value || isReassigningSpot.value
  );
  const isMutatingStep2 = computed((): boolean => createEventMutation.isPending.value);
  const isMutatingStep3 = computed((): boolean => patchContainerMutation.isPending.value);
  const isMutating = computed(
    (): boolean => isMutatingStep1.value || isMutatingStep2.value || isMutatingStep3.value
  );

  function isMutatingStep(s: WizardStep): boolean {
    if (s === WIZARD_STEPS.LOCATION) {
      return isMutatingStep1.value;
    }
    if (s === WIZARD_STEPS.STATUS) {
      return isMutatingStep2.value;
    }
    return isMutatingStep3.value;
  }

  function reset(): void {
    step.value = WIZARD_STEPS.LOCATION;
    selectedStatus.value = null;
    selectedState.value = null;
    locationOutcome.value = null; // locationOutcomeLabel resets via computed
    stepErrors.value[WIZARD_STEPS.LOCATION] = null;
    stepErrors.value[WIZARD_STEPS.STATUS] = null;
    stepErrors.value[WIZARD_STEPS.STATE] = null;
  }

  async function submitMissing(): Promise<void> {
    const detail = dialogProps.value;
    if (isNil(detail) || isMutatingStep1.value || isLocationRefused()) {
      return;
    }
    stepErrors.value[WIZARD_STEPS.LOCATION] = null;

    try {
      await recordOutcomeMutation.mutateAsync({
        taskId: detail.taskId,
        warehouseId: detail.warehouseId,
        yardTaskCheckId: detail.checkId,
        assetContainerId: detail.assetContainerId,
        outcome: YardTaskCheckLocationOutcome.MISSING
      });
      finishCheck();
      try {
        trackYardTaskAssetReportedMissing({
          yard_task_id: detail.taskId,
          asset_container_id: detail.assetContainerId,
          warehouse_id: detail.warehouseId,
          entry_point: entryPoint
        });
      } catch {
        // analytics failure must never block panel close
      }
    } catch {
      stepErrors.value[WIZARD_STEPS.LOCATION] = 'missingError';
    }
  }

  async function confirmLocation(): Promise<void> {
    const detail = dialogProps.value;
    if (isNil(detail) || isMutatingStep1.value || isLocationRefused()) {
      return;
    }
    stepErrors.value[WIZARD_STEPS.LOCATION] = null;

    try {
      await recordOutcomeMutation.mutateAsync({
        taskId: detail.taskId,
        warehouseId: detail.warehouseId,
        yardTaskCheckId: detail.checkId,
        assetContainerId: detail.assetContainerId,
        outcome: YardTaskCheckLocationOutcome.CONFIRMED
      });
      locationOutcome.value = LocationOutcome.Confirmed;
      step.value = WIZARD_STEPS.STATUS;
    } catch {
      stepErrors.value[WIZARD_STEPS.LOCATION] = 'confirmedError';
    }
  }

  function openSpotPicker(): void {
    if (isLocationRefused()) {
      return;
    }
    triggerSpotDialog({
      includeClosedSpots: 'disabled',
      includeAssignedSpots: 'disabled',
      includeReservedSpots: 'disabled',
      includeAssociatedToActiveMoveTasks: 'disabled',
      assetType: asset.value?.type,
      onSpotSelected: async (spot: ISpot) => {
        if (isLocationRefused()) {
          return;
        }
        const detail = dialogProps.value;
        if (isNil(detail)) {
          return;
        }
        stepErrors.value[WIZARD_STEPS.LOCATION] = null;

        const assignmentId = resolveActiveSpotAssignmentId(asset.value);

        try {
          if (isNil(assignmentId)) {
            await createAssignmentMutation.mutateAsync({
              taskId: detail.taskId,
              assetContainerId: detail.assetContainerId,
              spotId: spot.id
            });
          } else {
            await reassignMutation.mutateAsync({
              taskId: detail.taskId,
              assignmentId,
              spotId: spot.id
            });
          }
          // Updating the location implicitly confirms it for the check — record
          // a CONFIRMED outcome so the task isn't left without one.
          await recordOutcomeMutation.mutateAsync({
            taskId: detail.taskId,
            warehouseId: detail.warehouseId,
            yardTaskCheckId: detail.checkId,
            assetContainerId: detail.assetContainerId,
            outcome: YardTaskCheckLocationOutcome.CONFIRMED
          });
          locationOutcome.value = LocationOutcome.Updated;
          step.value = WIZARD_STEPS.STATUS;
        } catch {
          stepErrors.value[WIZARD_STEPS.LOCATION] = 'reassignError';
        }
      }
    });
  }

  function setStatus(status: string): void {
    if (status !== WIZARD_SKIP && !WIZARD_STATUS_OPTIONS.includes(status as WizardStatus)) {
      return;
    }
    selectedStatus.value = status as WizardStatus | typeof WIZARD_SKIP;
    stepErrors.value[WIZARD_STEPS.STATUS] = null;
  }

  async function goToStep3(): Promise<void> {
    const detail = dialogProps.value;
    if (isNil(detail) || isNil(selectedStatus.value) || isMutatingStep2.value) {
      return;
    }
    stepErrors.value[WIZARD_STEPS.STATUS] = null;

    if (selectedStatus.value === WIZARD_SKIP) {
      step.value = WIZARD_STEPS.STATE;
      return;
    }

    try {
      await createEventMutation.mutateAsync({
        taskId: detail.taskId,
        assetContainerId: detail.assetContainerId,
        eventType: selectedStatus.value
      });
      step.value = WIZARD_STEPS.STATE;
    } catch {
      stepErrors.value[WIZARD_STEPS.STATUS] = 'eventError';
    }
  }

  function setState(state: string): void {
    if (
      state !== WIZARD_SKIP &&
      !Object.values(AssetContainerState).includes(state as AssetContainerState)
    ) {
      return;
    }
    selectedState.value = state as AssetContainerState | typeof WIZARD_SKIP;
    stepErrors.value[WIZARD_STEPS.STATE] = null;
  }

  async function submitComplete(): Promise<void> {
    const detail = dialogProps.value;
    if (isNil(detail) || isNil(selectedState.value) || isMutatingStep3.value) {
      return;
    }
    stepErrors.value[WIZARD_STEPS.STATE] = null;

    if (selectedState.value === WIZARD_SKIP) {
      finishCheck();
      return;
    }

    try {
      await patchContainerMutation.mutateAsync({
        taskId: detail.taskId,
        assetContainerId: detail.assetContainerId,
        state: selectedState.value
      });
      finishCheck();
    } catch {
      stepErrors.value[WIZARD_STEPS.STATE] = 'stateError';
    }
  }

  return {
    state: {
      step,
      selectedStatus,
      selectedState,
      locationOutcome,
      locationOutcomeLabel,
      isMutating,
      isMutatingStep,
      isReassigningSpot,
      stepErrors
    },
    mutations: {
      setStatus,
      setState,
      reset
    },
    workflows: {
      submitMissing,
      confirmLocation,
      openSpotPicker,
      goToStep3,
      submitComplete
    }
  };
}
